import React from 'react';
import { Plus } from '@phosphor-icons/react';
import { Button, FlowLineageNode, VerticalEdge, edgeAnimationStyles } from '@chatsdet/ui';
import type { FlowLineage } from '../hooks/useFlowLineage';

/**
 * Fan-out dependency tree for flow lineage.
 *
 * Reuses the real workflow canvas primitives — {@link FlowLineageNode} (built on
 * the shared `WorkflowNodeWrapper`) and {@link VerticalEdge} — so it is visually
 * identical to the flow designer, but lays them out as a proper tiered tree
 * rather than the authoring canvas's single chain. That matters because the
 * authoring layout can only branch `condition` nodes into columns, so N flows
 * depending on one seed would collapse into a misleading vertical line.
 *
 * Orientation follows run order: a dependency (seed) a flow needs sits ABOVE it,
 * the flows that depend on this one sit BELOW, and every edge points downward
 * (dependency → the flows that depend on it), matching the canvas's top→bottom
 * execution reading.
 */

interface LineageTreeProps {
  lineage: FlowLineage;
  focusId: string;
  /** Build the designer href for a flow — the ↗ affordance opens it in a new tab. */
  flowHref: (id: string) => string;
  prunableIds: ReadonlySet<string>;
  onPrune: (flowId: string) => void;
  /** Open the "add a flow that depends on this one" palette (quick-add affordance). */
  onAddDependent: () => void;
}

const NODE_W = 240;
const EST_NODE_H = 96;
const ROW_GAP = 64;
const COL_GAP = 40;
const SIDE_PAD = 56;
// Clears the floating view-toggle / "Add dependent" toolbar overlaying the top.
const TOP_PAD = 116;

/** Assign each flow a signed tier: dependencies above the focus, dependents below. */
function computeLevels(lineage: FlowLineage, focusId: string) {
  const { nodes, edges } = lineage;
  const dirOf = new Map(nodes.map((n) => [n.id, n.direction]));
  const level = new Map<string, number>([[focusId, 0]]);

  for (let pass = 0; pass < nodes.length + 2; pass++) {
    let changed = false;
    for (const e of edges) {
      // e.from depends on e.to → the dependency (e.to) sits above its dependent.
      if (level.has(e.from) && dirOf.get(e.to) === 'downstream') {
        const want = level.get(e.from)! - 1;
        if (!level.has(e.to) || want < level.get(e.to)!) {
          level.set(e.to, want);
          changed = true;
        }
      }
      if (level.has(e.to) && dirOf.get(e.from) === 'upstream') {
        const want = level.get(e.to)! + 1;
        if (!level.has(e.from) || want > level.get(e.from)!) {
          level.set(e.from, want);
          changed = true;
        }
      }
    }
    if (!changed) break;
  }
  for (const n of nodes) {
    if (!level.has(n.id)) {
      level.set(n.id, n.direction === 'downstream' ? -1 : n.direction === 'upstream' ? 1 : 0);
    }
  }
  return level;
}

/** Order the nodes in each tier to sit under their anchor-side neighbours. */
function orderColumns(lineage: FlowLineage, level: Map<string, number>) {
  const adj = new Map<string, string[]>();
  for (const n of lineage.nodes) adj.set(n.id, []);
  for (const e of lineage.edges) {
    adj.get(e.from)?.push(e.to);
    adj.get(e.to)?.push(e.from);
  }

  const rows = new Map<number, string[]>();
  for (const n of lineage.nodes) {
    const lvl = level.get(n.id)!;
    (rows.get(lvl) ?? rows.set(lvl, []).get(lvl)!).push(n.id);
  }

  const xIndex = new Map<string, number>();
  const ordered = new Map<number, string[]>();
  const nameOf = new Map(lineage.nodes.map((n) => [n.id, n.name]));
  const byDistance = [...rows.keys()].sort((a, b) => Math.abs(a) - Math.abs(b));

  for (const lvl of byDistance) {
    const anchorSide = lvl - Math.sign(lvl); // tier one step toward the focus
    const bary = (id: string) => {
      const neighbours = (adj.get(id) ?? [])
        .filter((m) => level.get(m) === anchorSide)
        .map((m) => xIndex.get(m))
        .filter((v): v is number => v !== undefined);
      return neighbours.length
        ? neighbours.reduce((a, b) => a + b, 0) / neighbours.length
        : Number.POSITIVE_INFINITY;
    };
    const row = [...rows.get(lvl)!].sort((a, b) => {
      const d = bary(a) - bary(b);
      return Number.isFinite(d) && d !== 0 ? d : (nameOf.get(a)! < nameOf.get(b)! ? -1 : 1);
    });
    row.forEach((id, i) => xIndex.set(id, i - (row.length - 1) / 2));
    ordered.set(lvl, row);
  }
  return ordered;
}

export const LineageTree: React.FC<LineageTreeProps> = ({
  lineage,
  focusId,
  flowHref,
  prunableIds,
  onPrune,
  onAddDependent,
}) => {
  const nodeRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const [heights, setHeights] = React.useState<Map<string, number>>(new Map());

  const level = React.useMemo(() => computeLevels(lineage, focusId), [lineage, focusId]);
  const rows = React.useMemo(() => orderColumns(lineage, level), [lineage, level]);

  // Edge endpoints depend on measured node heights; remeasure when the set changes.
  React.useLayoutEffect(() => {
    const next = new Map<string, number>();
    nodeRefs.current.forEach((el, id) => next.set(id, el.offsetHeight));
    const changed =
      next.size !== heights.size ||
      [...next].some(([id, h]) => heights.get(id) !== h);
    if (changed) setHeights(next);
  });

  const { positions, width, height } = React.useMemo(() => {
    const levels = [...rows.keys()].sort((a, b) => a - b);
    const widest = Math.max(1, ...levels.map((l) => rows.get(l)!.length));
    const contentW = widest * NODE_W + (widest - 1) * COL_GAP;
    const centerX = SIDE_PAD + contentW / 2;

    const positions = new Map<string, { x: number; y: number; h: number }>();
    let y = TOP_PAD;
    for (const lvl of levels) {
      const row = rows.get(lvl)!;
      const rowH = Math.max(...row.map((id) => heights.get(id) ?? EST_NODE_H));
      const rowW = row.length * NODE_W + (row.length - 1) * COL_GAP;
      const startX = centerX - rowW / 2;
      row.forEach((id, i) => {
        positions.set(id, { x: startX + i * (NODE_W + COL_GAP), y, h: heights.get(id) ?? EST_NODE_H });
      });
      y += rowH + ROW_GAP;
    }
    return { positions, width: contentW + SIDE_PAD * 2, height: y };
  }, [rows, heights]);

  const hasOutgoing = React.useMemo(
    () => new Set(lineage.edges.map((e) => e.to)),
    [lineage.edges]
  );
  const hasIncoming = React.useMemo(
    () => new Set(lineage.edges.map((e) => e.from)),
    [lineage.edges]
  );

  const focusPos = positions.get(focusId);

  return (
    <div className="flex-1 h-full overflow-auto bg-muted/20 rounded-lg">
      <style>{edgeAnimationStyles}</style>
      <div className="relative mx-auto" style={{ width, height, minWidth: '100%' }}>
        {/* Edge layer: dependency (above) → dependent (below) = run order. */}
        <svg className="absolute inset-0" width={width} height={height} style={{ pointerEvents: 'none' }}>
          {lineage.edges.map((e) => {
            const dep = positions.get(e.to); // dependency (upper)
            const dependent = positions.get(e.from); // dependent (lower)
            if (!dep || !dependent) return null;
            return (
              <VerticalEdge
                key={`${e.to}->${e.from}:${e.nodeId}`}
                id={`${e.to}->${e.from}:${e.nodeId}`}
                sourceNodeId={e.to}
                targetNodeId={e.from}
                sourceX={dep.x + NODE_W / 2}
                sourceY={dep.y + dep.h}
                targetX={dependent.x + NODE_W / 2}
                targetY={dependent.y}
              />
            );
          })}
        </svg>

        {/* Node layer: the real FlowLineageNode card. */}
        {lineage.nodes.map((n) => {
          const pos = positions.get(n.id);
          if (!pos) return null;
          const isFocus = n.id === focusId;
          return (
            <div
              key={n.id}
              ref={(el) => {
                if (el) nodeRefs.current.set(n.id, el);
                else nodeRefs.current.delete(n.id);
              }}
              className="absolute"
              style={{ left: pos.x, top: pos.y, width: NODE_W }}
            >
              <FlowLineageNode
                nodeId={n.id}
                data={{
                  flowType: n.flowType,
                  name: n.name,
                  isFocus,
                  hasTargetHandle: hasIncoming.has(n.id),
                  hasSourceHandle: hasOutgoing.has(n.id),
                  viewFlowHref: isFocus ? undefined : flowHref(n.id),
                  onDelete: prunableIds.has(n.id) ? () => onPrune(n.id) : undefined,
                }}
              />
            </div>
          );
        })}

        {/* Quick-add: a flow that depends on this one, to the right of the root flow. */}
        {focusPos && (
          <div
            className="absolute"
            style={{ left: focusPos.x + NODE_W + 12, top: focusPos.y + focusPos.h / 2 - 16 }}
          >
            <Button
              variant="outline"
              size="sm"
              iconOnly
              tooltip="Add a flow that depends on this"
              aria-label="Add a flow that depends on this"
              onPress={onAddDependent}
            >
              <Plus size={14} weight="bold" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
